import * as tokens from '@tokens';
import type { BadgeCommonProps } from '@components/Badge/BaseBadge';
import { BaseBadge } from '@components/Badge/BaseBadge';
import { useStrings } from '@hooks/useStrings';
import { useInterpolate } from '@hooks/useInterpolate';

export interface PlanBadgeProps extends Omit<BadgeCommonProps, 'as'> {
  /**
   * Plan used for the tag.
   */
  kind: 'basic' | 'plus' | 'business' | 'scale';
  /**
   * If the plan is used in trial mode.
   */
  isInTrial?: boolean;
  /**
   * Callback when the plan is pressed.
   */
  onClick?: () => void;
}

const TAG_COLOR: Record<PlanBadgeProps['kind'], string> = {
  basic: tokens.color.bermuda,
  plus: tokens.color.lilac,
  business: tokens.color.beer,
  scale: tokens.color.azure,
};

/**
 * Small labels to display the user's plan, e.g. "business".
 *
 * {@link https://zeroheight.com/76c439891/p/05c5b9-badge/t/653147 Usage guidelines}
 *
 * {@link https://storybook.hotjar.com/?path=/docs/components-planbadge--basic-plan-badge Code documentation}
 *
 * @example
 * <PlanBadge kind="business" />
 */
export const PlanBadge = ({
  kind,
  size = 'm',
  isInTrial = false,
  onClick,
  className,
}: PlanBadgeProps): JSX.Element => {
  const { planBadge } = useStrings();
  const interpolate = useInterpolate();

  return (
    <BaseBadge
      size={size}
      kind="custom"
      color={TAG_COLOR[kind]}
      className={className}
      data-testid={`plan-tag-${kind}`}
      {...(onClick && { as: 'button', onClick, type: 'button' })}
    >
      {isInTrial ? interpolate(planBadge.trialKind, { kind }) : kind}
    </BaseBadge>
  );
};
